import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import type { StoryStepId } from "../data/steps";

type StoryChapterProps = {
  readonly id: StoryStepId;
  readonly label: string;
  readonly hint: string;
  /** Rendered on first paint instead of waiting for the reader to scroll. */
  readonly eager?: boolean;
  /** Forced open because the reader jumped here from the summary. */
  readonly opened?: boolean;
  /** The drawn path has reached this chapter's node. */
  readonly reached?: boolean;
  readonly children: ReactNode;
};

const HAS_OBSERVER = typeof IntersectionObserver !== "undefined";

/**
 * Latches to true once the element enters the given margin, then stops
 * observing. Without an IntersectionObserver the answer never changes, so each
 * caller picks the fallback that degrades gracefully.
 */
function useEnteredView(
  ref: RefObject<HTMLElement | null>,
  rootMargin: string,
  { enabled, fallback }: { readonly enabled: boolean; readonly fallback: boolean },
) {
  const [entered, setEntered] = useState(fallback);

  useEffect(() => {
    const node = ref.current;
    if (!enabled || entered || !node || !HAS_OBSERVER) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setEntered(true);
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, enabled, entered, rootMargin]);

  return entered;
}

/**
 * One leg of the scrolled journey: a node and an elbow drawn in the left
 * gutter, then the step itself. Steps far below the fold stay unmounted so the
 * six dashboards (maps, charts, queries) don't all load at once.
 */
export function StoryChapter({
  id,
  label,
  hint,
  eager = false,
  opened = false,
  reached = false,
  children,
}: StoryChapterProps) {
  const ref = useRef<HTMLElement | null>(null);
  const near = useEnteredView(ref, "800px 0px", {
    enabled: !eager && !opened,
    fallback: false,
  });
  const shown = useEnteredView(ref, "0px 0px -20% 0px", {
    enabled: true,
    fallback: !HAS_OBSERVER,
  });
  const mounted = eager || opened || near;

  return (
    <section
      ref={ref}
      id={`etape-${id}`}
      className="nbs-story-chapter"
      data-step={id}
      data-reached={reached ? "true" : "false"}
      data-visible={shown ? "true" : "false"}
    >
      <span className="nbs-story-chapter__mark" aria-hidden="true">
        <span className="nbs-story-chapter__elbow" />
        <span className="nbs-story-chapter__node" />
      </span>

      <div className="nbs-story-chapter__content">
        {mounted ? (
          children
        ) : (
          <div className="nbs-story-chapter__pending">
            <p className="nbs-story-step__eyebrow">
              Étape {id} · {hint}
            </p>
            <p className="nbs-story-chapter__pending-title">{label}</p>
            <span className="nbs-story-chapter__pending-bar" />
            <span className="nbs-story-chapter__pending-bar" />
          </div>
        )}
      </div>
    </section>
  );
}
